import { differenceBy } from 'lodash'
import { useMemo } from 'react'
import { AnyToken } from 'src/common/types/models/Tokens'
import { TokenSelectorGroup } from '../components/modals/TokenMultiSelectorModal'
import { filterByCriteria, filterByGroup } from './filters'
import { useNotOwnedTokens } from './useNotOwnedTokens'
import { useOwnedTokens } from './useOwnedTokens'

interface UseFilteredTokensReturned {
  ownedTokens: AnyToken[]
  notOwnedTokens: AnyToken[]
  loading: boolean
}

export const useFilteredTokens = (
  debouncedSearch: string,
  group: TokenSelectorGroup,
  exclude?: AnyToken
): UseFilteredTokensReturned => {
  const {
    notOwnedTokens,
    investAssets,
    searchedToken,
    loading: notOwnedTokensLoading,
  } = useNotOwnedTokens(debouncedSearch)

  const { ownedTokens, loading: ownedTokensLoading } = useOwnedTokens()

  // Filtered owned tokens
  const filteredOwnedTokens = useMemo(() => {
    if (searchedToken) return []
    const groupedTokens = filterByGroup(ownedTokens, group, investAssets)
    return filterByCriteria(groupedTokens, exclude, debouncedSearch)
  }, [
    searchedToken,
    ownedTokens,
    group,
    investAssets,
    exclude,
    debouncedSearch,
  ])

  // Filtered not owned tokens
  const filteredNotOwnedTokens = useMemo(() => {
    if (searchedToken) return [searchedToken]
    const notInOwned = differenceBy(
      notOwnedTokens,
      filteredOwnedTokens,
      'contractAddress'
    )
    const notOwned = filterByGroup(notInOwned, group, investAssets)
    return filterByCriteria(notOwned, exclude, debouncedSearch)
  }, [
    searchedToken,
    notOwnedTokens,
    filteredOwnedTokens,
    group,
    investAssets,
    exclude,
    debouncedSearch,
  ])

  // Overall loading state
  const loading = notOwnedTokensLoading || ownedTokensLoading

  return {
    ownedTokens: filteredOwnedTokens,
    notOwnedTokens: filteredNotOwnedTokens,
    loading,
  }
}
